import type { Config } from '@puckeditor/core';

import { tabsConfig } from './tabs';
import { collapsibleContentConfig } from './collapsible-content';
import { buttonConfig } from './button';
import { flexLayoutConfig } from './flex-layout';
import { gridLayoutConfig } from './grid-layout';
import { headingConfig } from './heading';
import { heroConfig } from './hero';
import { buttonRowConfig } from './button-row';
import { imageConfig } from './image';
import { navCarouselConfig } from './nav-carousel';
import { paragraphConfig } from './paragraph';
import { productCarouselConfig } from './product-carousel/product-carousel';
import { sectionConfig } from './section';
import { slideshowConfig } from './slideshow';
import { spacerConfig } from './spacer';
import { splitBannerConfig } from './split-banner';
import { tileConfig } from './tile';
import { videoConfig } from './video';

type Components = NonNullable<Config['components']>;

/**
 * Generic page-building blocks, usable by any CMS model. A model opts in by
 * spreading this whole map into its Puck config, or by picking the entries it
 * wants — nothing here assumes a particular model.
 *
 * **Adding a block here reaches all four models, and that is a deliberate invariant rather than a
 * side effect.** `header` and `footer` spread this bundle wholesale, and their own tests assert
 * `registers exactly the same component set as standalone-page` under an explicit "Product
 * requirement" comment, deriving the expectation from standalone-page's config so it fails when a
 * block is added there but not mirrored. So a block cannot be given to standalone-page alone
 * without either mirroring it or retiring that requirement — which is a product decision, not a
 * side effect of adding a component.
 */
export const PAGE_BLOCKS: Components = {
  Hero: heroConfig,
  ButtonRow: buttonRowConfig,
  Section: sectionConfig,
  FlexLayout: flexLayoutConfig,
  GridLayout: gridLayoutConfig,
  Spacer: spacerConfig,
  Heading: headingConfig,
  Paragraph: paragraphConfig,
  Image: imageConfig,
  Button: buttonConfig,
  Slideshow: slideshowConfig,
  Video: videoConfig,
  SplitBanner: splitBannerConfig,
  Tile: tileConfig,
  CollapsibleContent: collapsibleContentConfig,
  Tabs: tabsConfig,
};

/**
 * Blocks whose primary content comes from the merchant's catalog. Kept out of PAGE_BLOCKS so a
 * model can take generic authoring blocks without also exposing catalog carousels. Slideshow stays
 * in PAGE_BLOCKS because its ordinary image rows render from authored props; only its optional
 * connected Special row resolves exact-store eligibility and otherwise fails closed.
 *
 * `NavCarousel` belongs here rather than in PAGE_BLOCKS because it resolves its tiles through
 * `useDispensaryCategories`, which needs a store to know which categories and collections exist and
 * which the shopper may see.
 */
export const COMMERCE_BLOCKS: Components = {
  ProductCarousel: productCarouselConfig,
  NavCarousel: navCarouselConfig,
};

/** Commerce-category block names, in the order they should appear in the panel. */
export const COMMERCE_BLOCK_NAMES = ['ProductCarousel'];

/**
 * Catalog-backed blocks whose items are navigation tiles rather than products. Kept out of
 * `COMMERCE_BLOCK_NAMES` because `menu-landing`'s panel asserts its Product Carousels category holds
 * exactly the one consolidated product block -- the ten per-type carousels it replaced are gone, not
 * hidden -- and a carousel of categories is a different kind of thing, not an eleventh type.
 */
export const NAV_BLOCK_NAMES = ['NavCarousel'];

/** Layout-category block names, in the order they should appear in the panel. */
export const PAGE_LAYOUT_BLOCK_NAMES = [
  'Hero',
  'Section',
  'FlexLayout',
  'GridLayout',
  'SplitBanner',
  'Spacer',
  'CollapsibleContent',
  'Tabs',
];

/** Content-category block names, in the order they should appear in the panel. */
export const PAGE_CONTENT_BLOCK_NAMES = [
  'Heading',
  'Paragraph',
  'Image',
  'Button',
  'ButtonRow',
  'Slideshow',
  'Tile',
  'Video',
];
